import React from 'react';
import { toast } from 'react-toastify';
import CustomToast from './CustomToast';
import { CloseMD, CircleCheck, CircleCloseFilled, CircleWarning, Info } from '../../normalizer';

/**
 * Default icons for different toast types
 * Using Font Awesome icons (already available in the project)
 */
const DefaultIcons = {
  success: <CircleCheck className="svg-primary-fill success" />,
  error: <CircleCloseFilled className="svg-primary-fill error" />,
  warning: <CircleWarning className="svg-primary-fill warning" />,
  info: <Info className="svg-primary-fill info" />,
  close: <CloseMD className="svg-primary-fill close" />,
};

/**
 * ToastService - A drop-in replacement for ToastStore from react-toasts
 * 
 * Usage:
 *   ToastService.success('Message')
 *   ToastService.error('Message', { iconLeft: <CustomIcon /> })
 *   ToastService.warning('Message', { title: 'Warning Title' })
 * 
 * Options:
 *   - iconLeft: React node for left icon (defaults to type-specific icon)
 *   - iconRight: React node for right icon
 *   - title: Optional title text
 *   - className: Additional CSS class
 *   - ...toastOptions: Any react-toastify options (autoClose, position, etc.)
 */
const ToastService = {
  success: (message, options = {}) => {
    const { iconLeft, iconRight, title, className, ...toastOptions } = options;
    return toast.success(
      <CustomToast
        message={message}
        iconLeft={iconLeft !== undefined ? iconLeft : DefaultIcons.success}
        iconRight={iconRight !== undefined ? iconRight : DefaultIcons.close}
        title={title}
        className={className}
      />,
      {
        className: 'gl-toast-container gl-toast-container--success',
        icon: false,
        closeButton: false,
        position: toast.POSITION.BOTTOM_CENTER,
        ...toastOptions,
      }
    );
  },

  error: (message, options = {}) => {
    const { iconLeft, iconRight, title, className, ...toastOptions } = options;
    return toast.error(
      <CustomToast
        message={message}
        iconLeft={iconLeft !== undefined ? iconLeft : DefaultIcons.error}
        iconRight={iconRight !== undefined ? iconRight : DefaultIcons.close}
        title={title}
        className={className}
      />,
      {
        className: 'gl-toast-container gl-toast-container--error',
        icon: false,
        closeButton: false,
        position: toast.POSITION.BOTTOM_CENTER,
        ...toastOptions,
      }
    );
  },

  warning: (message, options = {}) => {
    const { iconLeft, iconRight, title, className, ...toastOptions } = options;
    return toast.warning(
      <CustomToast
        message={message}
        iconLeft={iconLeft !== undefined ? iconLeft : DefaultIcons.warning}
        iconRight={iconRight !== undefined ? iconRight : DefaultIcons.close}
        title={title}
        className={className}
      />,
      {
        className: 'gl-toast-container gl-toast-container--warning',
        icon: false,
        closeButton: false,
        position: toast.POSITION.BOTTOM_CENTER,
        ...toastOptions,
      }
    );
  },

  info: (message, options = {}) => {
    const { iconLeft, iconRight, title, className, ...toastOptions } = options;
    return toast.info(
      <CustomToast
        message={message}
        iconLeft={iconLeft !== undefined ? iconLeft : DefaultIcons.info}
        iconRight={iconRight !== undefined ? iconRight : DefaultIcons.close}
        title={title}
        className={className}
      />,
      {
        className: 'gl-toast-container gl-toast-container--info',
        icon: false,
        closeButton: false,
        position: toast.POSITION.BOTTOM_CENTER,
        ...toastOptions,
      }
    );
  },

  /**
   * Custom toast with full control over appearance
   * @param {string} message 
   * @param {string} type - 'success' | 'error' | 'warning' | 'info' | 'default'
   * @param {object} options 
   */
  custom: (message, type = 'default', options = {}) => {
    const { iconLeft, iconRight, title, className, ...toastOptions } = options;
    const toastFn = type === 'default' ? toast : toast[type];
    
    return toastFn(
      <CustomToast
        message={message}
        iconLeft={iconLeft}
        iconRight={iconRight}
        title={title}
        className={className}
      />,
      {
        className: `gl-toast-container gl-toast-container--${type}`,
        ...toastOptions,
      }
    );
  },

  /**
   * Dismiss a specific toast or all toasts
   * @param {string|number} toastId - Optional toast ID to dismiss
   */
  dismiss: (toastId) => {
    if (toastId) {
      toast.dismiss(toastId);
    } else {
      toast.dismiss();
    }
  },
};

export default ToastService;

